import React, { useMemo, useState } from 'react';
import { Database, RefreshCw } from 'lucide-react';

// OP_09 / EX_05 — Peer pro-forma comparison (shared between surfaces).
// Range-and-dot distribution rows plus the readout table, fed by a company
// metrics object {roa, rotce, nim, efficiency, cet1, loanGrowth}.
// Peer set is mocked with visible stub status (no live S&P claim).

export const PEER_PRO_FORMA_SET = [
  { peer: 'Regions Financial', source: 'Mock earnings call pro forma', roa: 1.14, rotce: 15.1, nim: 3.56, efficiency: 56.7, cet1: 10.5, loanGrowth: 2.6 },
  { peer: 'Fifth Third', source: 'Mock earnings call pro forma', roa: 1.2, rotce: 16.4, nim: 3.42, efficiency: 54.8, cet1: 10.7, loanGrowth: 3.1 },
  { peer: 'Citizens', source: 'Mock earnings call pro forma', roa: 1.05, rotce: 13.8, nim: 3.18, efficiency: 59.4, cet1: 10.2, loanGrowth: 2.1 },
  { peer: 'M&T Bank', source: 'Mock earnings call pro forma', roa: 1.22, rotce: 15.9, nim: 3.62, efficiency: 55.5, cet1: 11.1, loanGrowth: 2.4 },
  { peer: 'Synovus', source: 'Mock earnings call pro forma', roa: 1.12, rotce: 14.8, nim: 3.37, efficiency: 58.1, cet1: 10.4, loanGrowth: 3.4 }
];

const PEER_METRICS = [
  { key: 'roa', label: 'ROA', favorable: 'higher' },
  { key: 'rotce', label: 'ROTCE', favorable: 'higher' },
  { key: 'nim', label: 'NIM', favorable: 'higher' },
  { key: 'efficiency', label: 'Efficiency ratio', favorable: 'lower' },
  { key: 'cet1', label: 'CET1', favorable: 'higher' },
  { key: 'loanGrowth', label: 'Loan growth', favorable: 'balanced' }
];

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};
const fmt = (value, digits = 2) => `${Number(value).toFixed(digits)}%`;

const ForecastPeerProForma = ({ company, contextLabel = 'Company forecast' }) => {
  const [refresh, setRefresh] = useState({
    status: 'Stub data loaded',
    lastUpdated: 'Awaiting S&P Global connector',
    source: 'Mocked peer pro-formas (visible stub)'
  });

  const rows = useMemo(
    () =>
      PEER_METRICS.filter((metric) => company[metric.key] !== undefined && company[metric.key] !== null)
        .map((metric) => {
          const values = PEER_PRO_FORMA_SET.map((peer) => peer[metric.key]);
          const min = Math.min(...values);
          const max = Math.max(...values);
          const midpoint = median(values);
          const value = company[metric.key];
          const spread = Math.max(max - min, 0.01);
          const position = clamp(((value - min) / spread) * 100, 0, 100);
          const variance = value - midpoint;
          const favorable =
            metric.favorable === 'lower' ? value <= midpoint
              : metric.favorable === 'higher' ? value >= midpoint
                : Math.abs(variance) <= 1.5;
          return {
            ...metric,
            value, min, max, median: midpoint, position, variance, favorable,
            peerPositions: PEER_PRO_FORMA_SET.map((peer) => ({
              peer: peer.peer,
              value: peer[metric.key],
              position: clamp(((peer[metric.key] - min) / spread) * 100, 0, 100)
            }))
          };
        }),
    [company]
  );

  return (
    <>
      <div className="forecast-peer-actions">
        <span className="forecast-health-chip neutral">Illustrative peer pro-formas</span>
        <button
          type="button"
          className="forecast-icon-button"
          title="Stubbed refresh from S&P Global Market Intelligence"
          onClick={() =>
            setRefresh({
              status: 'Stub refresh complete',
              lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
              source: 'Mocked peer pro-formas (visible stub)'
            })
          }
        >
          <RefreshCw size={15} />
          Refresh from S&P Global
        </button>
      </div>

      <div className="forecast-peer-meta">
        <div>
          <Database size={16} />
          <span>{refresh.status}</span>
          <small>{refresh.lastUpdated}</small>
        </div>
        <div>
          <span>Source</span>
          <small>{refresh.source}</small>
        </div>
        <div>
          <span>Peer set</span>
          <small>{PEER_PRO_FORMA_SET.map((peer) => peer.peer).join(', ')}</small>
        </div>
      </div>

      <div className="forecast-peer-distribution-list">
        {rows.map((row) => (
          <div className="forecast-peer-distribution-row" key={`distribution-${row.key}`}>
            <div className="forecast-peer-distribution-label">
              <span>{row.label}</span>
              <strong>{fmt(row.value)}</strong>
              <small>
                Median {fmt(row.median)} / range {fmt(row.min)} to {fmt(row.max)}
              </small>
            </div>
            <div
              className="forecast-peer-distribution-track"
              role="img"
              aria-label={`${row.label}: company ${fmt(row.value)} versus peer range ${fmt(row.min)} to ${fmt(row.max)}.`}
            >
              <span className="forecast-peer-range-line" />
              {row.peerPositions.map((peer) => (
                <span
                  className="forecast-peer-dot"
                  key={`${row.key}-${peer.peer}`}
                  style={{ left: `${peer.position}%` }}
                  title={`${peer.peer}: ${fmt(peer.value)}`}
                />
              ))}
              <span className="forecast-peer-marker peer-median" style={{ left: '50%' }} title="Peer median" />
              <span className="forecast-peer-marker company" style={{ left: `${row.position}%` }} title={contextLabel} />
            </div>
            <div className="forecast-peer-distribution-readout">
              <span className={`forecast-validation ${row.favorable ? 'validated' : 'warning'}`}>
                {row.favorable ? 'Peer-aligned' : 'Watch variance'}
              </span>
              <small>{row.variance >= 0 ? '+' : ''}{fmt(row.variance)} vs median</small>
            </div>
          </div>
        ))}
      </div>

      <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
        <table className="forecast-table forecast-peer-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>{contextLabel}</th>
              <th>Peer median</th>
              <th>Peer range</th>
              <th>Position</th>
              <th>Readout</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td><strong>{row.label}</strong></td>
                <td>{fmt(row.value)}</td>
                <td>{fmt(row.median)}</td>
                <td>{fmt(row.min)} to {fmt(row.max)}</td>
                <td>
                  <div
                    className="forecast-peer-range"
                    role="img"
                    aria-label={`${row.label}: company ${fmt(row.value)}, peer range ${fmt(row.min)} to ${fmt(row.max)}.`}
                  >
                    <span className="forecast-peer-range-line" />
                    <span className="forecast-peer-marker peer-median" style={{ left: '50%' }} />
                    <span className="forecast-peer-marker company" style={{ left: `${row.position}%` }} />
                  </div>
                </td>
                <td>
                  <span className={`forecast-validation ${row.favorable ? 'validated' : 'warning'}`}>
                    {row.favorable ? 'Peer-aligned' : 'Watch variance'}
                  </span>
                  <small>{row.variance >= 0 ? '+' : ''}{fmt(row.variance)} vs median</small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ForecastPeerProForma;
